import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConsentDecisionInput, ConsentListQuery, RequestConsentInput } from '@teentack/shared';
import { jobKeys } from '@/features/jobs/use-jobs';
import {
  decideConsent,
  fetchConsentRequests,
  fetchMyConsentRequests,
  requestConsent,
} from './consent.api';

export const consentKeys = {
  all: ['consent'] as const,
  queue: (query: Partial<ConsentListQuery>) => ['consent', 'queue', query] as const,
  mine: (query: Partial<ConsentListQuery>) => ['consent', 'mine', query] as const,
};

/** Parent accounts only — `enabled` stops other roles calling it at all. */
export function useConsentQueue(query: Partial<ConsentListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: consentKeys.queue(query),
    queryFn: () => fetchConsentRequests(query),
    enabled,
    staleTime: 15_000,
  });
}

export function useMyConsentRequests(query: Partial<ConsentListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: consentKeys.mine(query),
    queryFn: () => fetchMyConsentRequests(query),
    enabled,
  });
}

export function useRequestConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestConsentInput) => requestConsent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consentKeys.all });
      // A job card shows consent state, so the browse list is stale now.
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useDecideConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ consentId, ...input }: ConsentDecisionInput & { consentId: string }) =>
      decideConsent(consentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consentKeys.all });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
